/**
 * Web helper: pick an image (camera or gallery) and return a base64 data URL.
 * Uses <input type="file"> with capture attribute on mobile.
 * Downsizes images to max 1600px on the longest side to keep uploads small.
 *
 * iOS/Capacitor reliability fix:
 *   The <input> element MUST be attached to the DOM before ``click()``.
 *   On iOS WKWebView (Capacitor iOS shell) an in-memory input that never
 *   enters the document tree can drop the ``change`` event when the
 *   webview is suspended (camera opens) and restored. Attaching the
 *   element (hidden) keeps the DOM node alive across the suspend/resume
 *   cycle so ``onchange`` fires reliably after the user validates the
 *   camera capture. We also clean up after ``change`` OR after focus is
 *   restored to detect cancellation on iOS < 17 which does not emit the
 *   ``cancel`` event.
 */
export function pickImage({ fromCamera = false, aspect } = {}) {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    if (fromCamera) input.capture = "environment";
    // Hidden but attached to DOM — required for iOS WKWebView reliability.
    input.style.position = "fixed";
    input.style.left = "-9999px";
    input.style.top = "0";
    input.style.opacity = "0";
    input.style.pointerEvents = "none";
    input.setAttribute("aria-hidden", "true");
    document.body.appendChild(input);

    let settled = false;
    const cleanup = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener("focus", onFocusReturn);
      try { document.body.removeChild(input); } catch { /* already removed */ }
    };
    const finish = (value) => {
      if (settled) return;
      cleanup();
      resolve(value);
    };
    // Cancellation heuristic: when the file picker closes without a
    // selection the browser gives focus back to the window WITHOUT firing
    // ``change``. We poll one frame later to see if a file was picked;
    // if not, resolve null. iOS 17+ additionally emits ``cancel``, we
    // handle it explicitly.
    const onFocusReturn = () => {
      // Delay so ``change`` has a chance to fire first.
      setTimeout(() => {
        if (settled) return;
        if (!input.files || input.files.length === 0) finish(null);
      }, 500);
    };
    input.oncancel = () => finish(null);
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return finish(null);
      try {
        const dataUrl = await processImageFile(file, { aspect });
        finish(dataUrl);
      } catch {
        finish(null);
      }
    };
    window.addEventListener("focus", onFocusReturn, { once: true });
    input.click();
  });
}

async function downsizeToDataUrl(file, { aspect } = {}) {
  return processImageFile(file, { aspect });
}

/**
 * Same downsize/crop pipeline as pickImage(), but starts from a File object
 * (e.g. selected via a dedicated <input type="file">). Returns a base64
 * JPEG data URL, or null on failure.
 */
export async function processImageFile(file, { aspect } = {}) {
  const MAX = 1600;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const el = new Image();
      el.onload = () => res(el);
      el.onerror = rej;
      el.src = url;
    });
    let { width, height } = img;
    // Optional cropping to aspect ratio (e.g. [4,3])
    let sx = 0, sy = 0, sw = width, sh = height;
    if (aspect && aspect.length === 2) {
      const [aw, ah] = aspect;
      const targetRatio = aw / ah;
      const currentRatio = width / height;
      if (currentRatio > targetRatio) {
        sw = height * targetRatio;
        sx = (width - sw) / 2;
      } else {
        sh = width / targetRatio;
        sy = (height - sh) / 2;
      }
    }
    // Downsize to MAX on longest side
    let dw = sw, dh = sh;
    if (Math.max(dw, dh) > MAX) {
      const scale = MAX / Math.max(dw, dh);
      dw = Math.round(dw * scale);
      dh = Math.round(dh * scale);
    }
    const canvas = document.createElement("canvas");
    canvas.width = dw;
    canvas.height = dh;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, dw, dh);
    return canvas.toDataURL("image/jpeg", 0.75);
  } finally {
    URL.revokeObjectURL(url);
  }
}
